import { ContentService } from '@blk/cwp-ng-framework';
import { AbstractRenderer, Alignment, BusinessComponent, BusinessComponentOptions, CellPanel, ColumnPanel, RowPanel, StaticParagraphComponent } from '@blk/simba';
import { MarketViewsUtil } from '@ddt-web/common';
import { ColorsConfig } from '@ddt-web/config';
import { ComponentData, DateFormatter } from '@ddt-web/data-service';
import { DateUtil } from '@ddt-web/generic-utils';
import { IconRenderer } from '@ddt-web/pdf-utils';

import { PdfUtils } from '../helpers';
import { MarketViewsConfig } from '../helpers/config';

export class MarketViewsComponent extends BusinessComponent {
	private marketViewConfig: any = MarketViewsConfig;
	constructor(
		public name: string,
		public dataComponentId: string,
		public defaultTranslationFromConfig?: any,
		public showHeader: boolean = true,
		public asOfDate: string = null,
		public isReducedPadding: boolean = false,
		public visible: boolean = true
	) {
		super(name, 'BusinessComponent', BusinessComponentOptions.merge({ visible: visible, componentId: dataComponentId }));
	}

	public isComponentDataVisible(componentData: ComponentData): boolean {
		return Boolean(componentData && componentData.tabs && componentData.tabs[0]) || !!this.defaultTranslationFromConfig;
	}

	public buildComponent(componentData: ComponentData, contentSvc: ContentService): AbstractRenderer<SVGGElement>[] {
		if (this.isComponentDataVisible(componentData)) {
			this.initMarketViewConfigDcrContent(contentSvc);
			if (this.showHeader) {
				return [this.getHeader(), this.getSubHeader(), this.getTableHeader(), ...this.getLegends(), ...this.getTable(), this.getDisclosure()];
			} else {
				return [this.getAsOfDate(), ...this.getLegends(), ...this.getTable()];
			}
		}
		return [];
	}

	private getHeader(): CellPanel {
		return new CellPanel(
			[
				new StaticParagraphComponent(null, this.marketViewConfig.subTitleSection.heading, {}, { proportion: 100 }),
				new StaticParagraphComponent(null, this.marketViewConfig.subTitleSection.subHeading, {}, { proportion: 100, paddingBottom: 10 })
			],
			{ proportion: 100 },
			Alignment.Horizontal.Left
		);
	}

	private getSubHeader(): CellPanel {
		const rows = [];
		const summaryItems = PdfUtils.convertUnorderedListHtmlStringToArray(this.marketViewConfig.summary);
		summaryItems.forEach((summaryItem, index) => {
			rows.push(
				new RowPanel(
					'',
					[
						new ColumnPanel(
							'MVSubTextLeftColumn',
							[new IconRenderer('circle', 3, 3, '#000', {})],
							{ proportion: 5, paddingTop: index === 0 ? 20 : 5 },
							Alignment.Horizontal.Left,
							Alignment.Vertical.Top
						),
						new ColumnPanel(
							'MVSubTextRightColumn',
							[new StaticParagraphComponent(summaryItem, summaryItem, {}, {})],
							{ proportion: 95, paddingTop: index === 0 ? 16 : 0, paddingBottom: index === summaryItems.length - 1 ? 16 : 10 },
							Alignment.Horizontal.Left,
							Alignment.Vertical.Top
						)
					],
					{ marginTop: 1 },
					Alignment.Horizontal.Center,
					Alignment.Vertical.Top
				)
			);
		});
		return new CellPanel(rows, { proportion: 100, backgroundColor: ColorsConfig.OVERVIEW_BACKGROUND_COLOR, paddingLeft: 15, paddingRight: 15 });
	}

	private getAsOfDate(): StaticParagraphComponent {
		return new StaticParagraphComponent(
			'PDF_MARKET_AS_OF',
			'As of {0}',
			{ 0: new DateFormatter(DateUtil.MODELS_US_DATE_FORMAT).format(this.asOfDate) },
			{ paddingBottom: 4 }
		);
	}

	private getTableHeader(): RowPanel {
		return new RowPanel(
			'',
			[new StaticParagraphComponent(this.marketViewConfig.assetSectionHeader, this.marketViewConfig.assetSectionHeader, {}, {}, ['text-16', 'text-bold'])],
			{ paddingTop: 30, paddingBottom: 5, marginTop: 1 },
			Alignment.Horizontal.Left,
			Alignment.Vertical.Top
		);
	}

	private getLegends(): RowPanel[] {
		const rows = [];

		rows.push(
			new RowPanel(
				'',
				[
					new ColumnPanel('', [new IconRenderer('triangle-up', 10, 10, '#009639', {})], { proportion: 2 }, Alignment.Horizontal.Left, Alignment.Vertical.Top),
					new ColumnPanel(
						'',
						[new StaticParagraphComponent(this.marketViewConfig.weights.overweight, 'Overweight', {}, {})],
						{ proportion: 14, paddingLeft: 5, marginTop: 1 },
						Alignment.Horizontal.Left,
						Alignment.Vertical.Top
					),
					new ColumnPanel('', [new IconRenderer('triangle-down', 10, 10, '#B5121B', {})], { proportion: 2 }, Alignment.Horizontal.Left, Alignment.Vertical.Top),
					new ColumnPanel(
						'',
						[new StaticParagraphComponent(this.marketViewConfig.weights.underweight, 'Underweight', {}, {})],
						{ proportion: 14, paddingLeft: 5, marginTop: 1 },
						Alignment.Horizontal.Left,
						Alignment.Vertical.Top
					),
					new ColumnPanel('', [new IconRenderer('neutral', 10, 10, '#41B6E6', {})], { proportion: 2 }, Alignment.Horizontal.Left, Alignment.Vertical.Top),
					new ColumnPanel(
						'',
						[new StaticParagraphComponent(this.marketViewConfig.weights.neutral, 'Neutral', {}, {})],
						{ proportion: 14, paddingLeft: 5, marginTop: 1 },
						Alignment.Horizontal.Left,
						Alignment.Vertical.Top
					),
					new ColumnPanel('', [], { proportion: 52, marginTop: 1 }, Alignment.Horizontal.Left, Alignment.Vertical.Top)
				],
				{ paddingBottom: this.isReducedPadding ? 5 : 20, marginTop: 1 },
				Alignment.Horizontal.Center,
				Alignment.Vertical.Top
			)
		);

		return rows;
	}

	private getTable(): RowPanel[] {
		const rows = [];
		const paddingTop = this.isReducedPadding ? 5 : 20;
		const paddingBottom = this.isReducedPadding ? 0 : 10;
		this.marketViewConfig.assets.forEach((asset, index) => {
			rows.push(
				new RowPanel(
					'',
					[new StaticParagraphComponent(asset.header, asset.header, {}, {}, ['text-12', 'text-bold'])],
					{ paddingBottom: paddingBottom, proportion: 100, paddingTop: index > 0 ? paddingTop : 0 },
					Alignment.Horizontal.Left,
					Alignment.Vertical.Top
				)
			);
			rows.push(
				new RowPanel(
					'',
					[
						new ColumnPanel(
							'',
							[
								new StaticParagraphComponent(
									asset.tableHeaders[0],
									asset.tableHeaders[0],
									{},
									{},
									['text-bold']
								)
							],
							{ proportion: 5 },
							Alignment.Horizontal.Left,
							Alignment.Vertical.Top
						),
						new ColumnPanel(
							'',
							[
								new StaticParagraphComponent(
									asset.tableHeaders[1],
									asset.tableHeaders[1],
									{},
									{},
									['text-bold']
								)
							],
							{ proportion: 20 },
							Alignment.Horizontal.Left,
							Alignment.Vertical.Top
						),
						new ColumnPanel(
							'',
							[
								new StaticParagraphComponent(
									asset.tableHeaders[2],
									asset.tableHeaders[2],
									{},
									{},
									['text-bold']
								)
							],
							{ proportion: 75 },
							Alignment.Horizontal.Left,
							Alignment.Vertical.Top
						)
					],
					{ paddingBottom: this.isReducedPadding ? 0 : 10, borderBottom: 1, borderBottomColor: '#000', marginTop: 1 },
					Alignment.Horizontal.Center,
					Alignment.Vertical.Top
				)
			);

			asset.breakdowns.forEach(breakdown => {
				rows.push(
					new RowPanel(
						'',
						[
							this.getIndicatorType(breakdown.view),
							new ColumnPanel(
								'',
								[new StaticParagraphComponent(breakdown.region, breakdown.region, {}, {}, ['text-bold'])],
								{ proportion: 20 },
								Alignment.Horizontal.Left,
								Alignment.Vertical.Top
							),
							new ColumnPanel(
								'',
								[new StaticParagraphComponent(breakdown.commentary, breakdown.commentary, {}, {})],
								{ proportion: 75 },
								Alignment.Horizontal.Left,
								Alignment.Vertical.Top
							)
						],
						{ marginTop: this.isReducedPadding ? 0 : 9, paddingBottom: this.isReducedPadding ? 0 : 9, borderBottom: 0.25, borderBottomColor: '#616161' },
						Alignment.Horizontal.Center,
						Alignment.Vertical.Middle
					)
				);
			});
		});
		return rows;
	}

	private getDisclosure(): RowPanel {
		return new RowPanel(
			'',
			[
				new ColumnPanel('', [new IconRenderer('asterisk', 10, 10, '#A5A5A5', {})], { proportion: 5, paddingLeft: 3 }, Alignment.Horizontal.Left, Alignment.Vertical.Top),
				new ColumnPanel(
					'',
					[new StaticParagraphComponent(this.marketViewConfig.disclosure, this.marketViewConfig.disclosure, {}, {})],
					{ proportion: 95 },
					Alignment.Horizontal.Left,
					Alignment.Vertical.Top
				)
			],
			{ paddingTop: 20, marginTop: 1 },
			Alignment.Horizontal.Left,
			Alignment.Vertical.Top
		);
	}

	private initMarketViewConfigDcrContent(contentSvc: ContentService): void {
		this.marketViewConfig.title = contentSvc.get(this.marketViewConfig.title, contentSvc.get(this.marketViewConfig.title.replace('_PDF', ''), this.marketViewConfig.title));

		this.marketViewConfig.subTitleSection.heading = contentSvc.get(
			this.marketViewConfig.subTitleSection.heading,
			contentSvc.get(this.marketViewConfig.subTitleSection.heading.replace('_PDF', ''), this.marketViewConfig.subTitleSection.heading)
		);

		this.marketViewConfig.subTitleSection.subHeading = contentSvc.get(
			this.marketViewConfig.subTitleSection.subHeading,
			contentSvc.get(this.marketViewConfig.subTitleSection.subHeading.replace('_PDF', ''), this.marketViewConfig.subTitleSection.subHeading)
		);

		this.marketViewConfig.summary = contentSvc.get(
			this.marketViewConfig.summary,
			contentSvc.get(this.marketViewConfig.summary.replace('_PDF', ''), this.marketViewConfig.summary)
		);

		this.marketViewConfig.assetSectionHeader = contentSvc.get(
			this.marketViewConfig.assetSectionHeader,
			contentSvc.get(this.marketViewConfig.assetSectionHeader.replace('_PDF', ''), this.marketViewConfig.assetSectionHeader)
		);

		this.marketViewConfig.weights.overweight = contentSvc.get(
			this.marketViewConfig.weights.overweight,
			contentSvc.get(this.marketViewConfig.weights.overweight.replace('_PDF', ''), this.marketViewConfig.weights.overweight)
		);
		this.marketViewConfig.weights.underweight = contentSvc.get(
			this.marketViewConfig.weights.underweight,
			contentSvc.get(this.marketViewConfig.weights.underweight.replace('_PDF', ''), this.marketViewConfig.weights.underweight)
		);
		this.marketViewConfig.weights.neutral = contentSvc.get(
			this.marketViewConfig.weights.neutral,
			contentSvc.get(this.marketViewConfig.weights.neutral.replace('_PDF', ''), this.marketViewConfig.weights.neutral)
		);
		this.marketViewConfig.disclosure = contentSvc.get(
			this.marketViewConfig.disclosure,
			contentSvc.get(this.marketViewConfig.disclosure.replace('_PDF', ''), this.marketViewConfig.disclosure)
		);
		let assetClasses, allCellsDcr;
		if (this.defaultTranslationFromConfig?.rows?.length > 1) {
			[assetClasses, allCellsDcr] = MarketViewsUtil.getDataFromConfig(this.defaultTranslationFromConfig);
		} else {
			[assetClasses, allCellsDcr] = MarketViewsUtil.getAllCellsDcr(contentSvc);
		}
		this.marketViewConfig.assets = [];
		assetClasses.forEach((asset, assetIndex) => {
			const header = contentSvc.get(`${asset}_PDF`, contentSvc.get(asset, asset));
			this.marketViewConfig.assets.push({
				header: header,
				tableHeaders: allCellsDcr[assetIndex].header.map((tableHeader) => {
					return contentSvc.get(tableHeader, tableHeader);
				}),
				breakdowns: allCellsDcr[assetIndex].body.map(row => {
					const view: string = row[0];
					const region: string = row[1];
					const commentary: string = row[2];
					return {
						view: contentSvc.get(`${view}_PDF`, contentSvc.get(view, view)),
						region: contentSvc.get(`${region}_PDF`, contentSvc.get(region, region)),
						commentary: contentSvc.get(`${commentary}_PDF`, contentSvc.get(commentary, commentary))
					};
				})
			});
		});
	}

	private getIndicatorType(indicator: string): ColumnPanel {
		const indicatorString = indicator ? indicator.toLowerCase() : '';
		switch (indicatorString) {
			case 'overweight':
				return new ColumnPanel(
					'',
					[new IconRenderer('triangle-up', 10, 10, '#009639', {})],
					{ proportion: 5, paddingRight: 6, marginTop: 1 },
					Alignment.Horizontal.Left,
					Alignment.Vertical.Top
				);
			case 'underweight':
				return new ColumnPanel(
					'',
					[new IconRenderer('triangle-down', 10, 10, '#B5121B', {})],
					{ proportion: 5, paddingRight: 6, marginTop: 1 },
					Alignment.Horizontal.Left,
					Alignment.Vertical.Top
				);
			case 'asterisk':
				return new ColumnPanel(
					'',
					[new IconRenderer('asterisk', 10, 10, '#B5121B', {})],
					{ proportion: 5, paddingRight: 6, marginTop: 1 },
					Alignment.Horizontal.Left,
					Alignment.Vertical.Top
				);
			default:
				return new ColumnPanel(
					'',
					[new IconRenderer('neutral', 10, 10, '#41B6E6', {})],
					{ proportion: 5, paddingRight: 6, marginTop: 1 },
					Alignment.Horizontal.Left,
					Alignment.Vertical.Top
				);
		}
	}
}
